import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import { authAPI } from '../services/api';
import { isAuthenticated } from '../services/auth';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

const normalizeCode = (value = '') => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
const formatCode = (value = '') => {
  const normalized = normalizeCode(value);
  return normalized.length > 4 ? `${normalized.slice(0, 4)}-${normalized.slice(4)}` : normalized;
};

function PlayStationLinkPage() {
  const location = useLocation();
  const initialCode = new URLSearchParams(location.search).get('code') || '';
  const [code, setCode] = useState(formatCode(initialCode));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState(null);
  const authenticated = isAuthenticated();
  const redirect = `/playstation/link${code ? `?code=${encodeURIComponent(code)}` : ''}`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await authAPI.confirmPsnLink(code);
      setLink(response.data?.link || { linked: true });
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to link this PlayStation account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 9 } }}>
      <Box sx={{ p: { xs: 3, md: 5 }, borderRadius: '28px', border: '1px solid rgba(19,61,103,0.12)', bgcolor: '#fff', boxShadow: '0 28px 72px rgba(18,61,103,0.10)' }}>
        <Stack spacing={2.4}>
          <Box sx={{ width: 56, height: 56, borderRadius: '18px', display: 'grid', placeItems: 'center', bgcolor: '#e9f3ff', color: '#123d67' }}>
            <SportsEsportsRoundedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: publicHeadingFontFamily, fontSize: { xs: '2rem', md: '2.5rem' }, lineHeight: 1.05 }}>
              Link PlayStation Network
            </Typography>
            <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
              Enter the short code shown by Virtual Medic on your PS5. This is a one-time association; future console sign-ins use only the signed-in PlayStation account.
            </Typography>
          </Box>

          {link?.linked ? (
            <Alert severity="success">
              PlayStation Network{link.onlineId ? ` (${link.onlineId})` : ''} is linked. Return to your PS5 to continue.
            </Alert>
          ) : (
            <>
              {!authenticated ? (
                <Alert severity="info">
                  Sign in to the Virtual Medic account you want to use. University students should use their existing university account. If you are new to Virtual Medic, choose “Create new account” on your PS5 for the shortest setup.
                </Alert>
              ) : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="Link code"
                    value={code}
                    onChange={(event) => setCode(formatCode(event.target.value))}
                    placeholder="ABCD-EFGH"
                    inputProps={{ maxLength: 9, autoCapitalize: 'characters', spellCheck: false }}
                    sx={publicTextFieldSx}
                    fullWidth
                  />
                  {authenticated ? (
                    <Button type="submit" disabled={submitting || normalizeCode(code).length !== 8} sx={{ ...publicPrimaryButtonSx, minHeight: 50 }}>
                      {submitting ? 'Linking...' : 'Link account'}
                    </Button>
                  ) : (
                    <Stack spacing={1.2}>
                      <Button component={RouterLink} to="/login" state={{ redirect }} sx={{ ...publicPrimaryButtonSx, minHeight: 50 }}>
                        Sign in
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </>
          )}
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.6, fontFamily: publicBodyFontFamily }}>
            One PlayStation account can be linked to one Virtual Medic account. Your Virtual Medic password is never shared with PlayStation and changing it will not break console sign-in.
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}

export default PlayStationLinkPage;
